<template>
  <Listbox
    as="div"
    :modelValue="selectedOption"
    @update:modelValue="selectedOption = $event"
    class="w-100 position-relative"
    by="value"
    v-slot="{ open: isListboxOpen }"
  >
    <Float
      as="div"
      placement="bottom-start"
      :offset="5"
      flip
      shift
      adaptiveWidth
      enter="transition duration-200 ease-out"
      enter-from="opacity-0"
      enter-to="opacity-100"
      leave="transition duration-75 ease-in"
      leave-from="opacity-100"
      leave-to="opacity-0"
      :show="isListboxOpen"
    >
      <div class="position-relative">
        <ListboxButton
          class="form-control d-flex align-items-center justify-content-between"
          :class="{
            'form-control-sm': size === 'sm',
          }"
        >
          <span v-if="selectedOption" class="text-truncate">
            {{ selectedOption.label }}
          </span>
          <span v-else class="text-muted">
            {{ placeholder }}
          </span>
          <FontAwesomeIcon
            icon="fas fa-chevron-down"
            class="ms-2 flex-shrink-0"
            :class="{ 'rotate-180': isListboxOpen }"
          />
        </ListboxButton>
      </div>

      <ListboxOptions
        class="w-100 overflow-x-hidden overflow-y-auto text-break bg-white border rounded-2 shadow-sm p-0 mt-1"
        style="max-height: 300px; z-index: 1050"
        static
      >
        <div
          v-if="isLoading"
          class="px-3 py-2 text-muted d-flex align-items-center justify-content-center"
        >
          <LoadingIndicatorComponent inline :message="$t('common.loading')" />
        </div>
        <div
          v-else-if="isError"
          class="px-3 py-2 text-muted d-flex align-items-center justify-content-center"
        >
          <FontAwesomeIcon icon="fas fa-exclamation-triangle" class="me-2" />
          <span>{{ $t("common.unexpected_error") }}</span>
        </div>
        <div v-else-if="options.length > 0">
          <ListboxOption
            v-for="option in options"
            :key="option.value"
            :value="option"
            as="template"
            #default="{ active, selected }"
          >
            <li
              class="select-option-item d-flex align-items-center px-3 py-2 cursor-pointer"
              :class="{ 'bg-light': active }"
              tabindex="-1"
            >
              <div class="flex-grow-1">
                <div class="fw-medium text-wrap">
                  {{ option.label }}
                </div>
                <div
                  class="text-muted small"
                  v-if="option.description"
                  v-html="option.description"
                ></div>
              </div>
              <span v-if="selected" class="ms-2 text-primary">
                <FontAwesomeIcon icon="fas fa-check" />
              </span>
            </li>
          </ListboxOption>
        </div>
        <div v-else class="px-3 py-2 text-muted">
          {{ $t("common.no_results") }}
        </div>
      </ListboxOptions>
    </Float>
  </Listbox>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { computed } from "vue";
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from "@headlessui/vue";
import { Float } from "@headlessui-float/vue";

interface Option {
  label: string;
  value: any;
  description?: string;
}

const props = defineProps<{
  options: Option[];
  placeholder?: string;
  size?: "sm" | "md";
  isLoading?: boolean;
  isError?: boolean;
}>();

const model = defineModel<any>();

const selectedOption = computed<Option | undefined>({
  get() {
    return props.options.find((opt) => opt.value === model.value);
  },
  set(option?: Option) {
    model.value = option?.value;
  },
});
</script>

<style lang="scss" scoped>
.select-option-item {
  position: relative;
  z-index: 1;
}

.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}
</style>
